import React, { useState, useCallback, useRef, RefObject } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';
import { useDidUpdate } from 'rooks';
import { isNil } from 'lodash';

import {
  createCommentForRecruiter,
  getCommentsForRecruiter,
  updateCommentForRecruiter,
} from 'redux/reducers/recruiterSlice';
import {
  createCommentForCustomer,
  getCommentsForCustomer,
  updateCommentForCustomer,
} from 'redux/reducers/clientSlice';
import CommentInput from './CommentInput';
import CommentsList from './CommentsList';
import {
  MemberCommentsPagedResponseModel,
  MemberCommentViewModel,
} from 'api/adminApiClient';
import { ActivitiesSectionPageTypeEnum } from '../enums';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import {
  createCommentForAssignment,
  getCommentsForAssignment,
  updateCommentForAssignment,
} from 'redux/reducers/assignmentSlice';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { SubmitComment } from './CommentInput';

import styles from './CommentSection.module.less';

type Props = {
  entityId: string;
  type: ActivitiesSectionPageTypeEnum;
  scrollRef: RefObject<HTMLElement>;
  parentRef: RefObject<HTMLElement>;
};

export default function CommentsSection(props: Props) {
  const { entityId, type, parentRef, scrollRef } = props;
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const latestRequestId = useRef(0);
  const infiniteScrollRef = useRef<any>(null);
  const containerCommentsRef = useRef<HTMLDivElement>(null);

  const [isCommentCreating, seIsCommentCreating] = useState(false);
  const [comments, setComments] = useState<MemberCommentViewModel[]>([]);
  const [hasMore, setHasMore] = useState(true);

  useDidUpdate(() => {
    resetScroll();
  }, [entityId]);

  const resetScroll = async () => {
    await onLoadMore(0);
    handleScrollIntoListStart();
  };

  const onSetComments = useCallback(
    (
      result: PayloadAction<MemberCommentsPagedResponseModel>,
      pageNumber: number,
    ) => {
      const loadedComments = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const actualComments = pageNumber === 0 ? [] : comments;
      const newComments = [...actualComments, ...loadedComments];
      const hasMoreRows = newComments.length < totalCount;

      setHasMore(hasMoreRows);
      setComments(newComments);
    },
    [comments],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      let correctAction;
      switch (type) {
        case ActivitiesSectionPageTypeEnum.Member:
          correctAction = getCommentsForRecruiter;
          break;
        case ActivitiesSectionPageTypeEnum.Customer:
          correctAction = getCommentsForCustomer;
          break;
        case ActivitiesSectionPageTypeEnum.Assignment:
          correctAction = getCommentsForAssignment;
          break;

        default:
          correctAction = getCommentsForRecruiter;
      }

      const result = await dispatch(
        correctAction({
          entityId,
          pageNumber,
          requestId,
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (correctAction.fulfilled.match(result)) {
          onSetComments(result, pageNumber);
        } else {
          setHasMore(false);
        }
      }
    },
    [dispatch, onSetComments, type, entityId],
  );

  const onCreateComment = useCallback(
    async (values: SubmitComment) => {
      if (isCommentCreating) return;

      let createAction;
      switch (type) {
        case ActivitiesSectionPageTypeEnum.Customer:
          createAction = createCommentForCustomer;
          break;
        case ActivitiesSectionPageTypeEnum.Assignment:
          createAction = createCommentForAssignment;
          break;
        case ActivitiesSectionPageTypeEnum.Member:
        default:
          createAction = createCommentForRecruiter;
      }

      const data = {
        entityId,
        text: values.comment.trim(),
      };

      seIsCommentCreating(true);
      const result = await dispatch(createAction(data));
      seIsCommentCreating(false);

      if (createAction.fulfilled.match(result)) {
        await resetScroll();

        return true;
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);

        return false;
      }
    },
    // eslint-disable-next-line
    [type, entityId, dispatch, isCommentCreating, onLoadMore],
  );

  const onEditComment = useCallback(
    async (values: SubmitComment) => {
      if (isCommentCreating) return;

      let editAction;
      switch (type) {
        case ActivitiesSectionPageTypeEnum.Customer:
          editAction = updateCommentForCustomer;
          break;
        case ActivitiesSectionPageTypeEnum.Assignment:
          editAction = updateCommentForAssignment;
          break;
        case ActivitiesSectionPageTypeEnum.Member:
        default:
          editAction = updateCommentForRecruiter;
      }

      const data = {
        entityId,
        text: values.comment.trim(),
        commentId: values?.commentId,
      };

      seIsCommentCreating(true);
      const result = await dispatch(editAction(data));
      seIsCommentCreating(false);

      if (editAction.fulfilled.match(result)) {
        await resetScroll();

        return true;
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);

        return false;
      }
    },
    // eslint-disable-next-line
    [type, entityId, dispatch, isCommentCreating, onLoadMore],
  );

  const handleSubmit = useCallback(
    async (values: SubmitComment) => {
      const isEditMode = !isNil(values?.commentId);

      return isEditMode
        ? await onEditComment(values)
        : await onCreateComment(values);
    },
    [onEditComment, onCreateComment],
  );

  const handleScrollIntoListStart = useCallback(() => {
    // in case "start add comment section" scrolled and hidden in the top
    // we should scroll to start of comment section
    const shouldScroll =
      (scrollRef?.current?.scrollTop || 0) >
      (parentRef?.current?.offsetTop || 0);

    if (shouldScroll) {
      parentRef?.current?.scrollIntoView();
    }

    if (infiniteScrollRef?.current?.pageLoaded) {
      infiniteScrollRef.current.pageLoaded = 0;
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div ref={containerCommentsRef} className={styles.commentSection}>
      <CommentInput
        onSubmit={handleSubmit}
        onClick={handleScrollIntoListStart}
        scrollRef={scrollRef}
        parentRef={parentRef}
        comment={{ firstName: user?.firstName, lastName: user?.lastName }}
      />

      <div className={styles.commentsContainer}>
        <CommentsList
          comments={comments}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          scrollRef={scrollRef}
          infiniteScrollRef={infiniteScrollRef}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}
